import { useCallback, useState } from 'react';
import Moment from 'moment';

import {
  downloadAWSDoc,
  downloadPolicyCertificate,
} from '../../../../policies/PolicyService';
import { downloadQuoteProposal } from '../../../../inbox/QuotesService';
import { saveBlobFile } from '../../../../utils/appUtils';
import { getResponseStatus, manageAPIError } from '../../../../utils';
import useNotifications from '../../../../_lib/notifications/useNotifications';
import type { CoverageLine } from './coverage.types';

/** Shown whenever a document cannot be produced, however we found out. */
const UNAVAILABLE =
  'This document is not available yet. Your agent can send you a copy.';

/**
 * Whether this line has a document to hand over at all.
 *
 * A quote always has its proposal. A policy has a certificate only once one has
 * been generated, and `isCertificateAvailable` is the service's own answer to
 * that -- set when the document is uploaded, and what every other console
 * surface already gates on. Branching on `kind` alone offered proof of cover on
 * a bound, unpaid, unsigned policy, where no certificate exists and the request
 * 404s (FIR-21145).
 *
 * Deliberately not a status allowlist: that would duplicate a judgement the
 * service already makes, and would still 404 on an in-force policy whose
 * certificate has not been generated yet.
 *
 * Absent reads as unavailable. The service always sends the boolean, so
 * undefined means a record that never said, and offering the download anyway
 * is what caused the 404.
 */
export const hasDownloadableDocument = (line: CoverageLine): boolean => {
  if (line.kind === 'quote') {
    return true;
  }

  return line.isCertificateAvailable === true;
};

/**
 * Downloads the document behind a coverage line.
 *
 * The tile's secondary button is labelled "Certificate of insurance" or "Download quote" and
 * carries a download icon, but it was a link to the same route as the primary action -- so it
 * navigated and downloaded nothing. A control that says what it does not do is worse than no
 * control, hence a real implementation rather than a relabel.
 *
 * Both services hand back a signed URL that has to be fetched separately before it can be
 * saved, which is why this is three steps rather than one.
 */
export function useCoverageDownload() {
  const notifications = useNotifications();
  const [isDownloading, setIsDownloading] = useState(false);

  const download = useCallback(
    async (line: CoverageLine) => {
      // Not every payload carries these; without them the request would 404 rather than
      // fail informatively.
      if (!line.accountId || !line.id) {
        notifications.error(UNAVAILABLE);
        return;
      }

      setIsDownloading(true);

      try {
        const createdAt = Moment(line.createdOn).unix();
        // The certificate, not the whole policy packet: the button says "Certificate of
        // insurance", and downloadPolicyDocuments returns something else entirely.
        const { data } =
          line.kind === 'quote'
            ? await downloadQuoteProposal(line.accountId, line.id, createdAt)
            : await downloadPolicyCertificate(
                line.accountId,
                line.id,
                createdAt
              );

        const file = await downloadAWSDoc(data);

        saveBlobFile(
          file,
          `${line.companyName ?? 'Cowbell'}-${line.reference}`,
          '.pdf'
        );
      } catch (error) {
        // A 404 here is not a failure to report, it is the document not
        // existing -- and the body is a bare string, which manageAPIError
        // prefers over any fallback, so the service's own words reached the
        // insured verbatim. Keyed on the status, never `isAxiosError`: the
        // interceptor rejects with ApiError, which does not carry that flag.
        if (getResponseStatus(error) === 404) {
          notifications.error(UNAVAILABLE);

          return;
        }

        notifications.error(
          manageAPIError(
            error,
            line.kind === 'quote'
              ? 'Unable to download this quote'
              : 'Unable to download your certificate of insurance'
          )
        );
      } finally {
        setIsDownloading(false);
      }
    },
    [notifications]
  );

  return { download, isDownloading };
}

export default useCoverageDownload;
